import { bound } from "@frui.ts/helpers/dist";
import { Modules } from "config";
import { computed } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import EnumService from "services/enumService";
import ListViewModel from "viewModels/listViewModel";
import type { interfaces } from "inversify";
import type { IResults } from "data/types";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import type VehicleRegisterVehicleChangeResponse from "entities/vehicleRegisterVehicleChangeResponse";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import type VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";
import type VehicleRegisterVehicleChangeOrigin from "entities/vehicleRegisterVehicleChangeOrigin";
import type VehicleRegisterVehiclePropertyChangeDisplayType from "entities/vehicleRegisterVehiclePropertyChangeDisplayType";

export default class VehicleRegisterHistoriesViewModel extends ListViewModel<VehicleRegisterVehicleChangeResponse> {
  readonly modelName = "vehicleRegisterHistories";

  constructor(
    public vehicleRegister: VehicleRegisterVehicle,
    public localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: VehicleRegisterRepository,
    private enumService: EnumService
  ) {
    super();
    this.navigationName = "";
  }

  protected async fetchData(): Promise<IResults<VehicleRegisterVehicleChangeResponse>> {
    return await this.repository.getAllChanges(this.vehicleRegister);
  }

  @computed
  get transformedItems(): VehicleRegisterVehicleChangeResponse[] {
    return this.items.flatMap(item => {
      return item;
    });
  }

  @bound
  onClose() {
    this.requestClose();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleRegisterDetailHistory.${code}`, size);
  }

  translateChangeAttribute(attribute: keyof VehicleRegisterVehicleChangeResponse) {
    return this.localizationService.translateAttribute("vehicleRegisterHistories", attribute);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.name;
  }

  translateBoolean(boolean: string) {
    return this.localizationService.translateGeneral(`boolean.${boolean.toLowerCase()}`);
  }

  translateVehicleRegisterVehicleFuelType(fuelType: string) {
    const fuelTypes = fuelType.split(",") as VehicleRegisterVehicleFuelType[];
    const translation = fuelTypes.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  translateChangeType(status: VehicleRegisterVehiclePropertyChangeDisplayType) {
    return this.localizationService.translateEnum("vehicleRegisterVehiclePropertyChangeDisplayType", status);
  }

  translateChangeOrigin(status: VehicleRegisterVehicleChangeOrigin) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleChangeOrigin", status);
  }

  formatHeight(amount: number) {
    return `${amount} kg`;
  }

  parseDateTime(dateTime: string) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [date, time] = dateTime.split(" ");
    return date;
  }

  static Factory({ container }: interfaces.Context) {
    return (vehicleRegister: VehicleRegisterVehicle) =>
      new VehicleRegisterHistoriesViewModel(
        vehicleRegister,
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(VehicleRegisterRepository),
        container.get(EnumService)
      );
  }
}
